"use client";

import { useDeferredValue, useMemo, useState } from "react";
import { HighlightedText } from "@/components/tool-kit/highlighted-text";
import {
  Checkbox,
  DataTable,
  Notice,
  SelectField,
  StatCard,
  StatGrid,
  TextAreaField,
  TextField,
} from "@/components/tool-kit/primitives";
import {
  BUTTON_SECONDARY,
  HINT,
  LABEL,
  TABLE,
  TD,
  TH,
  WIDGET_H2,
  WIDGET_H3,
} from "@/components/tool-kit/styles";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { t, tPlural } from "@/lib/i18n/interpolate";
import { downloadText } from "@/lib/tool-kit/download";
import {
  buildPattern,
  type ConcordanceLine,
  type ConcordanceSort,
  collocates,
  concordance,
  concordanceCsv,
  keywordForms,
  MAX_LINES,
  sortLines,
} from "@/lib/tool-kit/text-analysis/concordance";

/** English strings. A translated content file overrides these through `ui`. */
export const CONCORDANCE_TOOL_STRINGS = {
  textLabel: "Text to search",
  textPlaceholder:
    "Paste a chapter, a set of articles, interview transcripts, or your own draft.",
  trySample: "Try a sample",
  clear: "Clear",
  queryLabel: "Word or phrase",
  queryPlaceholder: "memory",
  queryHint:
    "Use * for any letters: analy* finds analyse, analysis, and analytical.",
  matchCase: "Match case",
  wholeWords: "Whole words only",
  widthLabel:
    "one:Context: {count} word on each side|other:Context: {count} words on each side",
  sortLabel: "Sort lines by",
  sorts: {
    position: "Order in the text",
    left: "Word to the left",
    right: "Word to the right",
    keyword: "Matched form",
  } as Record<ConcordanceSort, string>,
  hideStopwords: "Leave function words (the, of, and…) out of the collocates",
  emptyPrompt: "Paste a text and enter a word or phrase to search for.",
  needLetters: "Enter a word or phrase with at least one letter or digit.",
  noMatches: "No matches for “{query}”.",
  results: "Results",
  statHits: "Hits",
  statRate: "Per 1,000 words",
  statForms: "Different forms",
  linesHeading: "Concordance lines",
  linesTruncated:
    "Showing the first {shown} of {total} lines. The CSV download has every line.",
  colNumber: "#",
  colLeft: "Left context",
  colKeyword: "Keyword",
  colRight: "Right context",
  downloadCsv: "Download CSV",
  csvFilename: "concordance.csv",
  formsLine: "Forms matched: {list}",
  formItem: "{form} ({count})",
  collocatesHeading: "Collocates in the context window",
  collocatesHint: "The words that occur most often near the keyword.",
  collocatesNone: "No collocates in the window.",
  colWord: "Word",
  colLeftCount: "Left",
  colRightCount: "Right",
  colTotal: "Total",
  showInText: "Show the matches in the full text",
  highlightLabel: "The full text with every match highlighted",
  sample:
    "Memory is not a single system. Working memory holds a few items for seconds, while long-term memory stores knowledge for years. Sleep supports memory consolidation, the process that turns fragile traces into durable ones.\n\nWithout sleep, working memory suffers first: tired participants hold fewer digits and lose them sooner. Long-term memory suffers later, when new learning fails to consolidate. Researchers therefore test memory at several delays, because each memory system fails on its own schedule.",
  sampleQuery: "memory",
} as const;

type Strings = typeof CONCORDANCE_TOOL_STRINGS;

const SORTS: ConcordanceSort[] = ["position", "left", "right", "keyword"];
const COLLOCATE_ROWS = 20;
const PER_THOUSAND = 1000;

function KwicTable({
  lines,
  strings,
}: {
  lines: readonly ConcordanceLine[];
  strings: Strings;
}) {
  const fmt = useToolNumber();
  return (
    <div className="max-h-[520px] overflow-auto rounded-lg border border-border bg-card">
      <table className={TABLE}>
        <thead>
          <tr>
            <th className={TH} scope="col">
              {strings.colNumber}
            </th>
            <th className={`${TH} text-right`} scope="col">
              {strings.colLeft}
            </th>
            <th className={`${TH} text-center`} scope="col">
              {strings.colKeyword}
            </th>
            <th className={TH} scope="col">
              {strings.colRight}
            </th>
          </tr>
        </thead>
        <tbody>
          {lines.slice(0, MAX_LINES).map((line, index) => (
            <tr key={line.start}>
              <td className={`${TD} text-muted-foreground`}>
                {fmt(index + 1)}
              </td>
              <td className={`${TD} whitespace-nowrap text-right`}>
                {line.left}
              </td>
              <td
                className={`${TD} whitespace-nowrap text-center font-semibold`}
              >
                {line.keyword}
              </td>
              <td className={`${TD} whitespace-nowrap`}>{line.right}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Collocates({
  lines,
  hideStopwords,
  strings,
}: {
  lines: readonly ConcordanceLine[];
  hideStopwords: boolean;
  strings: Strings;
}) {
  const fmt = useToolNumber();
  const rows = collocates(lines, hideStopwords).slice(0, COLLOCATE_ROWS);
  return (
    <div className="space-y-2">
      <h3 className={WIDGET_H3}>{strings.collocatesHeading}</h3>
      <p className={HINT}>{strings.collocatesHint}</p>
      {rows.length === 0 ? (
        <Notice>{strings.collocatesNone}</Notice>
      ) : (
        <DataTable
          head={[
            strings.colWord,
            strings.colLeftCount,
            strings.colRightCount,
            strings.colTotal,
          ]}
          rows={rows.map((row) => [
            row.word,
            fmt(row.left),
            fmt(row.right),
            fmt(row.total),
          ])}
        />
      )}
    </div>
  );
}

function Results({
  text,
  query,
  lines,
  totalWords,
  hideStopwords,
  strings,
}: {
  text: string;
  query: string;
  lines: readonly ConcordanceLine[];
  totalWords: number;
  hideStopwords: boolean;
  strings: Strings;
}) {
  const fmt = useToolNumber();
  if (lines.length === 0) {
    return (
      <section aria-live="polite">
        <Notice>{t(strings.noMatches, { query: query.trim() })}</Notice>
      </section>
    );
  }
  const forms = keywordForms(lines);
  const rate = (lines.length / Math.max(1, totalWords)) * PER_THOUSAND;
  const head = [
    strings.colNumber,
    strings.colLeft,
    strings.colKeyword,
    strings.colRight,
  ] as const;
  return (
    <section aria-live="polite" className="space-y-5">
      <h2 className={WIDGET_H2}>{strings.results}</h2>
      <StatGrid>
        <StatCard emphasis label={strings.statHits} value={fmt(lines.length)} />
        <StatCard
          label={strings.statRate}
          value={fmt(rate, { maximumFractionDigits: 1 })}
        />
        <StatCard label={strings.statForms} value={fmt(forms.length)} />
      </StatGrid>
      <div className="space-y-3">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h3 className={WIDGET_H3}>{strings.linesHeading}</h3>
          <button
            className={BUTTON_SECONDARY}
            onClick={() =>
              downloadText(
                strings.csvFilename,
                concordanceCsv(lines, head),
                "text/csv;charset=utf-8"
              )
            }
            type="button"
          >
            {strings.downloadCsv}
          </button>
        </div>
        {lines.length > MAX_LINES ? (
          <p className={HINT}>
            {t(strings.linesTruncated, {
              shown: fmt(MAX_LINES),
              total: fmt(lines.length),
            })}
          </p>
        ) : null}
        <KwicTable lines={lines} strings={strings} />
      </div>
      {forms.length > 1 ? (
        <p className="text-foreground text-sm">
          {t(strings.formsLine, {
            list: forms
              .map((form) =>
                t(strings.formItem, { form: form.form, count: fmt(form.count) })
              )
              .join(", "),
          })}
        </p>
      ) : null}
      <Collocates
        hideStopwords={hideStopwords}
        lines={lines}
        strings={strings}
      />
      <details className="rounded-lg border border-border bg-card p-3">
        <summary className={`${LABEL} cursor-pointer`}>
          {strings.showInText}
        </summary>
        <div className="mt-3">
          <HighlightedText
            label={strings.highlightLabel}
            matches={lines.map((line) => ({
              start: line.start,
              end: line.end,
              category: "hit",
            }))}
            text={text}
          />
        </div>
      </details>
    </section>
  );
}

export default function ConcordanceToolWidget() {
  const strings = useToolUi(CONCORDANCE_TOOL_STRINGS);
  const locale = useToolLocale();
  const fmt = useToolNumber();
  const [text, setText] = useState("");
  const [query, setQuery] = useState("");
  const [caseSensitive, setCaseSensitive] = useState(false);
  const [wholeWord, setWholeWord] = useState(true);
  const [width, setWidth] = useState(5);
  const [sort, setSort] = useState<ConcordanceSort>("position");
  const [hideStopwords, setHideStopwords] = useState(true);
  const deferredText = useDeferredValue(text);
  const deferredQuery = useDeferredValue(query);
  const result = useMemo(
    () =>
      concordance(deferredText, deferredQuery, {
        caseSensitive,
        wholeWord,
        width,
      }),
    [deferredText, deferredQuery, caseSensitive, wholeWord, width]
  );
  const sorted = useMemo(() => sortLines(result.lines, sort), [result, sort]);
  const validQuery =
    buildPattern(deferredQuery, { caseSensitive, wholeWord }) !== null;

  function body() {
    if (!(deferredText.trim() && deferredQuery.trim())) {
      return <Notice>{strings.emptyPrompt}</Notice>;
    }
    if (!validQuery) {
      return <Notice>{strings.needLetters}</Notice>;
    }
    return (
      <Results
        hideStopwords={hideStopwords}
        lines={sorted}
        query={deferredQuery}
        strings={strings}
        text={deferredText}
        totalWords={result.totalWords}
      />
    );
  }

  return (
    <>
      <TextAreaField
        id="kwic-text"
        label={strings.textLabel}
        onChange={setText}
        placeholder={strings.textPlaceholder}
        prose
        rows={10}
        value={text}
      />
      <div className="flex flex-wrap gap-3">
        <button
          className={BUTTON_SECONDARY}
          onClick={() => {
            setText(strings.sample);
            setQuery(strings.sampleQuery);
          }}
          type="button"
        >
          {strings.trySample}
        </button>
        <button
          className={BUTTON_SECONDARY}
          disabled={text.length === 0}
          onClick={() => setText("")}
          type="button"
        >
          {strings.clear}
        </button>
      </div>
      <div className="grid gap-5 sm:grid-cols-2">
        <TextField
          hint={strings.queryHint}
          id="kwic-query"
          label={strings.queryLabel}
          onChange={setQuery}
          placeholder={strings.queryPlaceholder}
          type="search"
          value={query}
        />
        <SelectField
          id="kwic-sort"
          label={strings.sortLabel}
          onChange={setSort}
          options={SORTS.map((value) => ({
            value,
            label: strings.sorts[value],
          }))}
          value={sort}
        />
        <div className="space-y-2">
          <label className={LABEL} htmlFor="kwic-width">
            {tPlural(strings.widthLabel, locale, width, { count: fmt(width) })}
          </label>
          <input
            className="w-full accent-primary"
            id="kwic-width"
            max={15}
            min={1}
            onChange={(event) => setWidth(Number(event.target.value))}
            type="range"
            value={width}
          />
        </div>
        <div className="space-y-2">
          <Checkbox
            checked={caseSensitive}
            label={strings.matchCase}
            onChange={setCaseSensitive}
          />
          <Checkbox
            checked={wholeWord}
            label={strings.wholeWords}
            onChange={setWholeWord}
          />
          <Checkbox
            checked={hideStopwords}
            label={strings.hideStopwords}
            onChange={setHideStopwords}
          />
        </div>
      </div>
      {body()}
    </>
  );
}
